import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from '@/hooks/use-toast';
import { emailCampaignsApi, type EmailCampaign } from '../api';
import { Loader2, Plus, Send } from 'lucide-react';

const STATUS_VARIANT: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  DRAFT: 'outline',
  READY: 'secondary',
  RUNNING: 'default',
  PAUSED: 'secondary',
  COMPLETED: 'default',
  COMPLETED_WITH_ERRORS: 'secondary',
  CANCELLED: 'outline',
  FAILED: 'destructive',
  WAITING_FOR_QUOTA: 'secondary',
};

export default function EmailCampaignsListPage() {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState<EmailCampaign[]>([]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res = await emailCampaignsApi.list();
        setItems(res.items);
      } catch (error: any) {
        toast({
          variant: 'destructive',
          title: 'Error',
          description: error?.response?.data?.message || 'No se pudieron cargar las campañas.',
        });
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  return (
    <Layout>
      <div className="container mx-auto px-4 py-8 space-y-6">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2">
              <Send className="h-7 w-7" /> Campañas de Email
            </h1>
            <p className="text-muted-foreground">Disparos masivos vía Amazon SES.</p>
          </div>
          <Link to="/admin/email-campaigns/new">
            <Button>
              <Plus className="h-4 w-4 mr-2" /> Nueva campaña
            </Button>
          </Link>
        </div>

        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nombre</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead>Destinatarios</TableHead>
                <TableHead>Entregados</TableHead>
                <TableHead>Bounces</TableHead>
                <TableHead>Falhas</TableHead>
                <TableHead>Fecha</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Cargando...
                  </TableCell>
                </TableRow>
              ) : items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                    No hay campañas creadas.
                  </TableCell>
                </TableRow>
              ) : (
                items.map((c) => (
                  <TableRow key={c.id}>
                    <TableCell>
                      <Link to={`/admin/email-campaigns/${c.id}`} className="hover:underline font-medium">
                        {c.name}
                      </Link>
                    </TableCell>
                    <TableCell>
                      <Badge variant={STATUS_VARIANT[c.status] || 'outline'}>{c.status}</Badge>
                    </TableCell>
                    <TableCell>{c.totalRecipients}</TableCell>
                    <TableCell>{c.deliveredCount}</TableCell>
                    <TableCell>{c.bounceCount}</TableCell>
                    <TableCell>{c.failedCount}</TableCell>
                    <TableCell>{new Date(c.createdAt).toLocaleDateString()}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Card>
      </div>
    </Layout>
  );
}
